import { useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { useTranslation } from "@/i18n/use-translation";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { authErrorMessage } from "@/lib/auth-error";

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    setLoading(false);

    if (error) {
      toast.error(authErrorMessage(t, error));
      return;
    }

    setSent(true);
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="text-lg font-medium">{t("auth.forgotTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {sent ? (
            <>
              <p className="text-sm text-muted-foreground">
                {t("auth.forgotSentPrefix")} <strong>{email}</strong>, {t("auth.forgotSentSuffix")}
              </p>
              <Link to="/login">
                <Button variant="outline" className="w-full">
                  {t("auth.backToSignIn")}
                </Button>
              </Link>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">{t("auth.forgotDescription")}</p>
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="email" className="text-sm font-medium">
                    {t("auth.email")}
                  </label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full">
                  {loading ? t("auth.forgotSending") : t("auth.forgotSubmit")}
                </Button>
              </form>
              <Link
                to="/login"
                className="text-center text-xs text-muted-foreground underline hover:text-foreground"
              >
                {t("auth.backToSignIn")}
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
